import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Medal, 
  Plus, 
  Minus, 
  X 
} from 'lucide-react';
import { useUserAuth } from '../../../context/userAuthContext';
import { useGolfScoring } from './GolfScoringContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const GolfScoringButton = ({ activityId }) => {
  const { user } = useUserAuth();
  const { gameSetup, setupGolfGame, updateTeamScore } = useGolfScoring();
  const [activity, setActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [isScoringModalOpen, setIsScoringModalOpen] = useState(false);
  const [holeCount, setHoleCount] = useState(9);
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState({ name: '', scoreAdmins: [] });
  const [activeHole, setActiveHole] = useState(0);
  const [attendeeData, setAttendeeData] = useState({});

  const fetchActivity = useCallback(async () => {
    if (!activityId) {
      console.error('No activityId provided');
      setIsLoading(false);
      return;
    }

    try {
      const activityDoc = await getDoc(doc(db, 'activities', activityId));
      if (activityDoc.exists()) {
        setActivity({ id: activityDoc.id, ...activityDoc.data() });
      } else {
        console.log('No such document!');
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setIsLoading(false);
    }
  }, [activityId]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const fetchUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  };

  useEffect(() => {
    const loadAttendeeData = async () => {
      if (activity && activity.attendees) {
        const attendeePromises = activity.attendees.map(attendee => fetchUserData(attendee));
        const attendeeResults = await Promise.all(attendeePromises);
        const newAttendeeData = {};
        attendeeResults.forEach((data, index) => {
          if (data) {
            newAttendeeData[activity.attendees[index]] = data;
          }
        });
        setAttendeeData(newAttendeeData);
      }
    };

    if (activity) {
      loadAttendeeData();
    }
  }, [activity]);

  if (isLoading) {
    return null;
  }

  if (!activity || activity.category !== 'Golf' || activity.creatorUID !== user?.uid) {
    return null;
  }

  const availableAttendees = activity.attendees || [];

  const handleAddTeam = () => {
    if (currentTeam.name && currentTeam.scoreAdmins.length > 0) {
      setTeams(prev => [...prev, { ...currentTeam, id: `team-${Date.now()}` }]);
      setCurrentTeam({ name: '', scoreAdmins: [] });
    }
  };

  const handleRemoveTeam = (index) => {
    setTeams(prev => prev.filter((_, i) => i !== index));
  };

  const handleToggleScoreAdmin = (attendeeUid) => {
    setCurrentTeam(prev => {
      const isSelected = prev.scoreAdmins.includes(attendeeUid);
      return {
        ...prev,
        scoreAdmins: isSelected
          ? prev.scoreAdmins.filter(id => id !== attendeeUid)
          : [...prev.scoreAdmins, attendeeUid]
      };
    });
  };

  const handleStartGame = () => {
    if (teams.length === 0) {
      alert('Please create at least one team before starting the game.');
      return;
    }

    const formattedTeams = teams.map(team => ({
      id: team.id,
      name: team.name,
      scoreAdmins: team.scoreAdmins,
    }));

    setupGolfGame(holeCount, formattedTeams);
    setIsSetupModalOpen(false);
  };

  const renderSetupModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Golf Game Setup</h2>
          <button 
            onClick={() => setIsSetupModalOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X />
          </button>
        </div>

        <div className="mb-4">
          <label className="block mb-2">Number of Holes</label>
          <div className="flex space-x-2">
            {[9, 18].map((count) => (
              <button
                key={count}
                onClick={() => setHoleCount(count)}
                className={`px-4 py-2 rounded-lg flex-1 ${
                  holeCount === count 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {count} Holes
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block mb-2">Create Teams</label>
          <div className="mb-2 flex space-x-2">
            <input 
              type="text" 
              placeholder="Team Name"
              value={currentTeam.name}
              onChange={(e) => setCurrentTeam(prev => ({ ...prev, name: e.target.value }))}
              className="flex-grow p-2 border rounded"
            />
            <button 
              onClick={handleAddTeam}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Add Team
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2 max-h-40 overflow-y-auto">
            {availableAttendees.map((attendeeUid) => {
              const userData = attendeeData[attendeeUid] || {};
              return (
                <button
                  key={attendeeUid}
                  onClick={() => handleToggleScoreAdmin(attendeeUid)}
                  className={`p-2 rounded flex items-center ${
                    currentTeam.scoreAdmins.includes(attendeeUid)
                      ? 'bg-green-100 border-green-500'
                      : 'bg-gray-100'
                  }`}
                >
                  <img 
                    src={userData.photoURL || 'https://via.placeholder.com/40'}
                    alt={userData.displayName || 'User'}
                    className="w-8 h-8 rounded-full mr-2"
                  />
                  {userData.displayName || 'Loading...'}
                </button>
              );
            })}
          </div>

          <div className="mt-4 max-h-40 overflow-y-auto">
            {teams.map((team, index) => (
              <div 
                key={team.id} 
                className="bg-gray-50 p-2 rounded mb-2 flex justify-between items-center"
              >
                <div>
                  <p className="font-semibold">{team.name}</p>
                  <p className="text-sm text-gray-500">
                    Admins: {team.scoreAdmins.map(uid => 
                      attendeeData[uid]?.displayName || 'Loading...'
                    ).join(', ')}
                  </p>
                </div>
                <button 
                  onClick={() => handleRemoveTeam(index)}
                  className="text-red-500"
                >
                  <X />
                </button>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handleStartGame}
          disabled={teams.length === 0}
          className="w-full bg-green-500 text-white py-3 rounded-lg disabled:opacity-50 flex items-center justify-center"
        >
          <Medal className="mr-2" /> Start Golf Game
        </button>
      </div>
    </div>
  );

  const renderScoringModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <Medal className="mr-2" /> Golf Scoring
          </h2>
          <button 
            onClick={() => setIsScoringModalOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X />
          </button>
        </div>

        <div className="flex overflow-x-auto mb-4 space-x-2">
          {Array.from({ length: gameSetup.holeCount }, (_, i) => (
            <button
              key={i}
              onClick={() => setActiveHole(i)}
              className={`px-3 py-1 rounded-lg ${
                activeHole === i 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Hole {i + 1}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {gameSetup.teams.map((team) => {
            const canEdit = team.scoreAdmins.includes(user?.uid);
            const currentHoleScore = team.holes?.[activeHole] || 0;

            return (
              <div 
                key={team.id} 
                className="bg-white rounded-xl border shadow-md p-4"
              >
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-semibold">{team.name}</h3>
                    <p className="text-sm text-gray-500">
                      Total Score: {team.totalScore || 0}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="mr-3 text-gray-600 font-medium">
                    Hole {activeHole + 1} Score:
                  </label>
                  <div className="flex items-center">
                    <button
                      onClick={() => updateTeamScore(team.id, activeHole, Math.max(0, currentHoleScore - 1))}
                      disabled={!canEdit}
                      className="p-1 bg-gray-100 rounded-l-lg disabled:opacity-50"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input 
                      type="number" 
                      value={currentHoleScore}
                      onChange={(e) => {
                        const score = Math.max(0, parseInt(e.target.value) || 0);
                        updateTeamScore(team.id, activeHole, score);
                      }}
                      className="w-16 p-2 text-center border-t border-b"
                      min="0"
                      disabled={!canEdit}
                    />
                    <button
                      onClick={() => updateTeamScore(team.id, activeHole, currentHoleScore + 1)}
                      disabled={!canEdit}
                      className="p-1 bg-gray-100 rounded-r-lg disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button 
        onClick={() => {
          if (!gameSetup.holeCount) {
            setIsSetupModalOpen(true);
          } else {
            setIsScoringModalOpen(true);
          }
        }}
        className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-green-600 transition-colors"
      >
        <Users className="mr-2" /> 
        {!gameSetup.holeCount ? 'Setup Golf Game' : 'Golf Scoring'}
      </button>

      {isSetupModalOpen && renderSetupModal()}
      {isScoringModalOpen && renderScoringModal()}
    </>
  );
};

export default GolfScoringButton;